/**
 * ChunkErrorBoundary.tsx - Error Boundary for Chunk Load Failures
 *
 * Catches errors when lazy-loaded components fail to load (network errors,
 * deployment mismatches, etc.) and displays a recovery UI instead of a blank page.
 *
 * This is a SAFETY component that must work even when CSS fails to load.
 * All styles are inline to ensure the fallback UI always renders.
 *
 * @see .planning/research/PITFALLS.md - Section #3: Missing Error Boundary
 * @see .planning/research/ARCHITECTURE.md - Error Handling patterns
 */

import React, { Component, ReactNode, ErrorInfo } from "react";

// ============================================================================
// TYPES
// ============================================================================

interface ChunkErrorBoundaryProps {
  /** Child components to render (typically Suspense + lazy components) */
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
  /** Optional callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ChunkErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ============================================================================
// INLINE STYLES (must work without CSS loading)
// ============================================================================

const containerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100svh",
  width: "100%",
  padding: "24px",
  boxSizing: "border-box",
  background: "#030712", // --hero-landing-bg
  fontFamily: "var(--font-sans)",
};

const contentStyle: React.CSSProperties = {
  textAlign: "center",
  maxWidth: "400px",
};

const iconStyle: React.CSSProperties = {
  width: "64px",
  height: "64px",
  marginBottom: "24px",
  opacity: 0.6,
};

const titleStyle: React.CSSProperties = {
  fontSize: "24px",
  fontWeight: 600,
  color: "#e2e8f0", // --color-text-secondary
  marginBottom: "12px",
  margin: "0 0 12px 0",
};

const messageStyle: React.CSSProperties = {
  fontSize: "16px",
  color: "#94a3b8", // --color-text-dim
  lineHeight: 1.5,
  marginBottom: "24px",
  margin: "0 0 24px 0",
};

const buttonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  padding: "12px 24px",
  fontSize: "14px",
  fontWeight: 600,
  fontFamily: "var(--font-sans)",
  color: "#ffffff",
  background: "linear-gradient(135deg, #3f9da1 0%, #2d7a7d 100%)", // Teal accent gradient
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  boxShadow: "0 4px 16px rgba(63, 157, 161, 0.3)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
};

const buttonHoverStyle: React.CSSProperties = {
  ...buttonStyle,
  transform: "translateY(-2px)",
  boxShadow: "0 6px 20px rgba(63, 157, 161, 0.4)",
};

// ============================================================================
// COMPONENT
// ============================================================================

export class ChunkErrorBoundary extends Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  constructor(props: ChunkErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  /**
   * Static method called when a descendant component throws an error.
   * Updates state to trigger fallback UI rendering.
   */
  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Called after an error has been thrown by a descendant component.
   * Use for logging/reporting.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error for debugging
    console.error("[ChunkErrorBoundary] Caught chunk load error:", error);
    console.error("[ChunkErrorBoundary] Component stack:", errorInfo.componentStack);

    // Call optional error callback
    this.props.onError?.(error, errorInfo);
  }

  /**
   * Reload the page to attempt recovery.
   * This clears all cached state and fetches fresh chunks.
   */
  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // If custom fallback provided, use it
      if (fallback) {
        return fallback;
      }

      // Default fallback UI with inline styles
      return (
        <DefaultFallback onReload={this.handleReload} error={this.state.error} />
      );
    }

    // No error - render children normally
    return children;
  }
}

// ============================================================================
// DEFAULT FALLBACK COMPONENT
// ============================================================================

interface DefaultFallbackProps {
  onReload: () => void;
  error?: Error | null;
}

function DefaultFallback({ onReload, error }: DefaultFallbackProps): JSX.Element {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        {/* Warning icon */}
        <svg
          style={iconStyle}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#94a3b8"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>

        <h2 style={titleStyle}>Something went wrong</h2>

        <p style={messageStyle}>
          The page failed to load completely. This can happen after updates or
          due to network issues. Please reload to continue.
        </p>

        {error && (
          <p style={{ ...messageStyle, fontSize: '11px', color: '#ef4444', fontFamily: 'var(--font-mono)', marginTop: '8px', wordBreak: 'break-all' as const }}>
            {error.name}: {error.message}
          </p>
        )}

        <button
          type="button"
          onClick={onReload}
          style={isHovered ? buttonHoverStyle : buttonStyle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12a9 9 0 11-9-9" />
            <path d="M21 3v6h-6" />
          </svg>
          Reload Page
        </button>
      </div>
    </div>
  );
}

export default ChunkErrorBoundary;
